import React from "react";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-6 py-16">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Privacy Policy</h1>
        <p className="text-sm text-slate-500 mb-10">Last updated: July 2026</p>

        <div className="space-y-8 text-slate-700 text-[15px] leading-relaxed">
          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-2">1. Introduction</h2>
            <p>
              EasyWaybill ("we", "our", "the App") is a Shopify application that helps merchants generate shipping waybills, 
              get live courier quotes, and sync shipping rates to their Shopify store. This Privacy Policy explains what data 
              we collect, how we use it, and your rights regarding that data.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-2">2. Data We Collect</h2>
            <p className="mb-3">When you install and use EasyWaybill, we collect and process the following data from your Shopify store:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Order Information:</strong> Order numbers, line items (product names, quantities, SKUs), order totals, and order status.</li>
              <li><strong>Customer Shipping Details:</strong> Recipient name, shipping address, phone number, and email address — used solely to generate waybills and shipping labels.</li>
              <li><strong>Product Data:</strong> Product names and SKUs for matching to package dimension presets.</li>
              <li><strong>Store Information:</strong> Shop domain, store name, and currency for API integration.</li>
              <li><strong>Account Credentials:</strong> Your courier account credentials (stored encrypted) to authenticate with courier APIs on your behalf.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-2">3. How We Use Your Data</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>To generate waybills and parcel labels for your shipments.</li>
              <li>To fetch live shipping quotes from courier partners.</li>
              <li>To sync shipping rates to your Shopify Delivery Profiles.</li>
              <li>To pre-populate waybill forms with order and customer details.</li>
              <li>To create collection manifests for courier pickup.</li>
              <li>To monitor shipping rate changes and notify you of price updates.</li>
            </ul>
            <p className="mt-3">
              We do <strong>not</strong> sell, rent, or share your data with third parties for marketing purposes. 
              Customer data is only transmitted to your configured courier partner for the purpose of generating waybills.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-2">4. Data Storage & Security</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>All data is stored in encrypted databases hosted on secure cloud infrastructure.</li>
              <li>Shopify access tokens and courier API credentials are encrypted at rest using industry-standard encryption (Fernet/AES-256).</li>
              <li>All data transmission occurs over HTTPS/TLS.</li>
              <li>We implement strict multi-tenant isolation — each merchant's data is segregated by organization.</li>
              <li>Authentication uses bcrypt password hashing and JWT tokens.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-2">5. Data Retention</h2>
            <p>
              We retain your data for as long as the App is installed on your store. Waybill history and shipping records 
              are kept for your reference and operational needs. When you uninstall the App:
            </p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Your Shopify access token is immediately revoked and deleted.</li>
              <li>Within 48 hours of uninstallation, all store-specific data is permanently deleted in compliance with Shopify's requirements.</li>
              <li>A minimal, non-personally-identifiable record (shop domain only) is retained to prevent trial abuse.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-2">6. Your Rights (GDPR & POPIA)</h2>
            <p className="mb-2">You have the right to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Access:</strong> Request a copy of all data we store about you or your customers.</li>
              <li><strong>Rectification:</strong> Request correction of inaccurate data.</li>
              <li><strong>Erasure:</strong> Request deletion of your data at any time.</li>
              <li><strong>Portability:</strong> Request your data in a machine-readable format.</li>
              <li><strong>Restriction:</strong> Request that we limit processing of your data.</li>
            </ul>
            <p className="mt-3">
              We comply with South Africa's Protection of Personal Information Act (POPIA) and the EU General Data 
              Protection Regulation (GDPR). Shopify's mandatory GDPR webhooks (customer data request, customer data 
              erasure, and shop data erasure) are fully implemented.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-2">7. Third-Party Services</h2>
            <p>EasyWaybill integrates with the following third-party services:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li><strong>Shopify:</strong> To read orders, products, and manage shipping profiles (governed by Shopify's privacy policy).</li>
              <li><strong>Courier Partners:</strong> To generate waybills and fetch shipping quotes. Only the data necessary for shipment (sender/recipient address, parcel dimensions) is transmitted.</li>
            </ul>
            <p className="mt-2">We do not integrate with any advertising, analytics, or tracking services.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-2">8. Cookies</h2>
            <p>
              We use a single secure, HttpOnly authentication cookie to maintain your login session. 
              We do not use tracking cookies, advertising cookies, or any third-party cookies.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-2">9. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated 
              "Last updated" date. Continued use of the App after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-2">10. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy or wish to exercise your data rights, please contact us at:
            </p>
            <p className="mt-2 font-medium">
              Email: <a href="mailto:support@easywaybill.co.za" className="text-[#16a34a] hover:underline">support@easywaybill.co.za</a>
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-slate-200 text-center text-sm text-slate-400">
          EasyWaybill &copy; {new Date().getFullYear()}. All rights reserved.
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
